import {
  Connection,
  Transaction,
  VersionedTransaction,
  TransactionInstruction,
  TransactionMessage,
  PublicKey,
  AddressLookupTableAccount,
} from '@solana/web3.js'

export const SOLANA_MAINNET_RPC =
  import.meta.env.VITE_SOLANA_RPC_URL?.startsWith('http')
    ? (import.meta.env.VITE_SOLANA_RPC_URL as string)
    : `${window.location.origin}${import.meta.env.VITE_SOLANA_RPC_URL ?? '/api/rpc'}`

export function newConnection(): Connection {
  return new Connection(SOLANA_MAINNET_RPC, 'confirmed')
}

export interface SubmitResult {
  signature: string
  explorerUrl: string
}

export function explorerUrl(signature: string): string {
  return `https://solscan.io/tx/${signature}`
}

export interface SignAndSubmitOptions {
  /** If set (e.g. from Jupiter swap response) — used to time out based on block height instead of wall-clock. */
  lastValidBlockHeight?: number
  /** Maximum wall-clock seconds to wait before giving up. Default 90. */
  maxWaitSeconds?: number
  /** Re-send the signed tx every N ms to survive RPC drops. Default 2000. */
  resendIntervalMs?: number
  /**
   * Skip the client-side pre-simulation. Use for Jupiter Lend SDK transactions
   * whose nested CPI chains (initTick / initBranch / initTickIdLiquidation +
   * Metaplex NFT mint) cause a false-positive ResultWithNegativeLamports during
   * simulation even when the wallet has ample SOL.
   */
  skipPreSimulation?: boolean
  /**
   * Skip the RPC preflight simulation on sendRawTransaction. Use alongside
   * skipPreSimulation for Jupiter Lend SDK deposit transactions: Solana's
   * simulation engine cannot correctly account for lamport flow through deeply
   * nested CPI chains (account created in one CPI then immediately used in
   * another), producing false-positive ResultWithNegativeLamports even with
   * ample SOL. The transaction succeeds on-chain; simulation is the issue.
   * Phantom's own transaction preview still runs for user visibility.
   */
  skipPreflight?: boolean
}

/**
 * Deserialize a base64-encoded transaction returned by any backend endpoint.
 * Tries V0 (VersionedTransaction) first; falls back to legacy Transaction for
 * responses from the Orca sidecar which uses maxSupportedTransactionVersion:'legacy'.
 */
export function deserializeV0(base64: string): VersionedTransaction | Transaction {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
  try {
    return VersionedTransaction.deserialize(bytes)
  } catch {
    return Transaction.from(bytes)
  }
}

/**
 * Build a VersionedTransaction from a list of instructions and optional
 * address-lookup-table accounts — used for Jupiter Lend borrow ops which
 * return raw ixs + ALTs from the SDK.
 */
export async function buildV0(
  payer: PublicKey,
  ixs: TransactionInstruction[],
  lookupTables: AddressLookupTableAccount[] = [],
  connection = newConnection(),
): Promise<VersionedTransaction> {
  const { blockhash } = await connection.getLatestBlockhash('confirmed')
  const msg = new TransactionMessage({
    payerKey: payer,
    recentBlockhash: blockhash,
    instructions: ixs,
  }).compileToV0Message(lookupTables)
  return new VersionedTransaction(msg)
}

export function friendlyTxError(err: any): string {
  const msg = err?.message ?? String(err)
  if (msg.includes('User rejected') || msg.includes('rejected') || msg.includes('denied') || msg.includes('User denied')) {
    return 'Transaction rejected in wallet'
  }
  if (msg.includes('Transaction expired')) return msg
  if (msg.includes('was not confirmed within')) return msg
  // SPL Token program custom error 1 = InsufficientFunds. Surfaces whenever a
  // token transfer asks for more than the source ATA holds. Common root cause:
  // /api/wallet merges wallet + staking, so MAX used to overshoot the wallet
  // ATA. Tx-building forms now pre-flight against RPC, but keep this mapping
  // as a safety net for any code path that still slips through.
  if (msg.includes('Transaction failed on-chain') && /[,{]"?Custom"?:1\b/.test(msg)) {
    return 'Insufficient token balance in your wallet ATA for this transfer. Check MAX vs. actual spendable holdings.'
  }
  // Runtime returns InsufficientFundsForRent when the fee payer can't cover the
  // rent-exempt minimum on a new account (most often the destination ATA the
  // swap/lend would create). Tells users the actionable thing — top up SOL —
  // instead of the raw {"InsufficientFundsForRent":{"account_index":0}} blob.
  if (msg.includes('InsufficientFundsForRent')) {
    return 'Not enough SOL in your wallet to cover transaction fees and rent for any new token account this swap would open. Top up at least 0.01 SOL and retry.'
  }
  // ResultWithNegativeLamports — System Program Custom 1, raised when an ATA-
  // create or position-init would leave the payer with negative lamports. The
  // chain message looks like:
  //   Program 11111111111111111111111111111111 failed: custom program error: 0x1
  //   | Program ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL failed
  // Initialising a brand-new Jupiter Lend position alone needs ~0.015 SOL of
  // rent (position PDA + NFT mint + token account + Metaplex metadata).
  if (
    msg.includes('11111111111111111111111111111111 failed: custom program error: 0x1') ||
    msg.includes('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL failed')
  ) {
    return 'Not enough SOL in your wallet to fund the rent for new accounts this transaction would create (associated token account or position NFT). Top up at least 0.02 SOL — opening a brand-new Jupiter Lend position needs ~0.015 SOL of rent on top of normal fees — and retry.'
  }
  if (msg.includes('insufficient')) return msg
  if (msg.includes('blockhash not found')) return 'Blockhash expired — please retry'
  return msg.slice(0, 300)
}
